import type * as React from "react";
import { cn } from "../lib/utils";

export interface KpiTileProps extends React.ComponentProps<"div"> {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  /** Positive/negative delta rendered under the value, e.g. "+4.2%". */
  delta?: { value: React.ReactNode; direction: "up" | "down" | "flat" };
}

/** Demo `.kpi` tile: 22px bold value, 12px muted label. */
export function KpiTile({ label, value, hint, icon, delta, className, ...props }: KpiTileProps) {
  return (
    <div
      data-slot="kpi-tile"
      className={cn("rounded-xl border bg-card p-4 text-card-foreground", className)}
      {...props}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-muted-foreground text-xs">{label}</div>
        {icon ? <div className="text-gold [&_svg]:size-4">{icon}</div> : null}
      </div>
      <div className="mt-1 font-bold text-[22px] leading-tight tabular">{value}</div>
      {delta ? (
        <div
          className={cn(
            "mt-1 text-xs tabular",
            delta.direction === "up" && "text-success",
            delta.direction === "down" && "text-danger",
            delta.direction === "flat" && "text-muted-foreground",
          )}
        >
          {delta.value}
        </div>
      ) : null}
      {hint ? <div className="mt-1 text-muted-foreground text-xs">{hint}</div> : null}
    </div>
  );
}
